import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import type { Tenant } from "../lib/types";
import { fmtDate } from "../lib/utils";
import { PageHeader, FilterChip } from "../components/core/Layout";
import { DataTable } from "../components/core/DataTable";
import { Button } from "../components/core/Button";
import { Input } from "../components/core/Field";
import { ClientWizard } from "../components/shared/ClientWizard";
import { StatusBadge } from "../components/core/Badges";
import { useTenants } from "../components/shared/TenantSelect";

export default function ClientsPage() {
  const { data, isLoading } = useTenants();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<"" | "active" | "inactive">("");
  const rows = (data || []).filter((t) => (!q || t.name.toLowerCase().includes(q.toLowerCase()) || t.slug.includes(q.toLowerCase())) && (!status || (status === "active") === t.is_active));
  const totals = { clients: data?.length || 0, vehicles: (data || []).reduce((s, t) => s + (t.vehicle_count || 0), 0), alerts: (data || []).reduce((s, t) => s + (t.open_alerts || 0), 0) };
  return (
    <div data-testid="clients-page">
      <PageHeader eyebrow="Client management" title="Clients" subtitle={`${totals.clients} client(s) · ${totals.vehicles} registered vehicles · ${totals.alerts} open alerts`}
        actions={<Button data-testid="create-client-button" onClick={() => setOpen(true)}><Plus className="h-4 w-4" />New client</Button>} />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative"><Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" /><Input data-testid="client-search-input" placeholder="Search clients…" value={q} onChange={(e) => setQ(e.target.value)} className="w-64 pl-8" /></div>
        {([["", "All"], ["active", "Active"], ["inactive", "Inactive"]] as const).map(([s, l]) => <FilterChip key={s} active={status === s} onClick={() => setStatus(s)} testId={`client-status-${s || "all"}`}>{l}</FilterChip>)}
      </div>
      <DataTable<Tenant>
        testId="clients-table" loading={isLoading} rows={rows} rowKey={(t) => t.id} rowTestId={(t) => `client-row-${t.id}`} onRowClick={(t) => navigate(`/bo/clients/${t.id}`)}
        columns={[
          { key: "name", header: "Client", render: (t) => (
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-md text-[10px] font-bold text-white" style={{ background: "var(--brand)" }}>{(t.branding?.display_name || t.name).slice(0, 2).toUpperCase()}</span>
              <div><p className="font-semibold text-slate-900">{t.name}</p><p className="font-mono text-[11px] text-slate-500">{t.slug}{t.registration_no ? ` · ${t.registration_no}` : ""}</p></div>
            </div>
          ) },
          { key: "contact", header: "Contact", render: (t) => <div><p>{t.contact_name || "—"}</p><p className="text-[11px] text-slate-500">{t.contact_email}</p></div> },
          { key: "vehicles", header: "Vehicles", className: "text-right", render: (t) => <span className="font-display font-bold" data-testid={`client-vehicle-count-${t.id}`}>{t.vehicle_count}</span> },
          { key: "users", header: "Admins", className: "text-right", render: (t) => t.user_count },
          { key: "alerts", header: "Open alerts", className: "text-right", render: (t) => <span className={t.open_alerts ? "font-semibold text-rose-700" : ""}>{t.open_alerts}</span> },
          { key: "modules", header: "Modules", render: (t) => <span className="text-xs text-slate-500">{t.modules_enabled.length} enabled</span> },
          { key: "created", header: "Since", render: (t) => <span className="text-xs">{fmtDate(t.created_at)}</span> },
          { key: "status", header: "Status", render: (t) => <StatusBadge status={t.is_active ? "active" : "inactive"} /> },
        ]}
        empty="No clients yet. Onboard the first client."
      />
      <ClientWizard open={open} onClose={() => setOpen(false)} />
    </div>
  );
}
